import React, { useState, useRef } from "react";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPaperPlane,
  faSpinner,
  faCheckCircle,
  faExclamationCircle,
} from "@fortawesome/free-solid-svg-icons";
import emailjs from "@emailjs/browser";

interface FormData {
  nome: string;
  cognome: string;
  email: string;
  telefono: string;
  servizio: string;
  messaggio: string;
}

type FormStatus = "idle" | "loading" | "success" | "error";

const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

const ContactForm: React.FC = () => {
  const formRef = useRef<HTMLFormElement>(null);

  const [formData, setFormData] = useState<FormData>({
    nome: "",
    cognome: "",
    email: "",
    telefono: "",
    servizio: "",
    messaggio: "",
  });

  const [errors, setErrors] = useState<Partial<FormData>>({});
  const [privacyConsent, setPrivacyConsent] = useState(false);
  const [formStatus, setFormStatus] = useState<FormStatus>("idle");

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name as keyof FormData]) {
      setErrors({ ...errors, [name]: undefined });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<FormData> = {};
    if (!formData.nome.trim()) newErrors.nome = "Il nome è obbligatorio";
    if (!formData.cognome.trim()) newErrors.cognome = "Il cognome è obbligatorio";
    if (!formData.email.trim()) {
      newErrors.email = "L'email è obbligatoria";
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(formData.email)) {
      newErrors.email = "Formato email non valido";
    }
    if (!formData.messaggio.trim()) newErrors.messaggio = "Il messaggio è obbligatorio";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validateForm() || !privacyConsent) return;
    setFormStatus("loading");

    try {
      const templateParams = {
        from_name: `${formData.nome} ${formData.cognome}`,
        reply_to: formData.email,
        telefono: formData.telefono,
        servizio: formData.servizio || "Non specificato",
        messaggio: formData.messaggio,
      };

      await emailjs.send(
        EMAILJS_SERVICE_ID!,
        EMAILJS_TEMPLATE_ID!,
        templateParams,
        EMAILJS_PUBLIC_KEY!
      );

      await new Promise((resolve) => setTimeout(resolve, 1500));
      setFormStatus("success");

      if (formRef.current) formRef.current.reset();
      setFormData({ nome: "", cognome: "", email: "", telefono: "", servizio: "", messaggio: "" });
      setPrivacyConsent(false);
      setTimeout(() => setFormStatus("idle"), 5000);
    } catch (error) {
      console.error("Errore nell'invio del form:", error);
      setFormStatus("error");
      setTimeout(() => setFormStatus("idle"), 5000);
    }
  };

  const inputClasses = (hasError: boolean) =>
    `w-full p-3.5 border ${hasError ? "border-red-400" : "border-light-border"} rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary bg-light-background text-sm transition-all duration-200`;

  return (
    <motion.div
      initial={{ opacity: 0, x: 50 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.8, delay: 0.2 }}
      className="lg:w-2/3"
    >
      <div className="bg-white p-8 md:p-10 rounded-2xl shadow-card border border-light-border/50">
        <h2 className="text-2xl font-extrabold mb-2 text-light-text font-display">Richiedi una Consulenza Gratuita</h2>
        <p className="mb-2 text-light-muted text-sm">
          Compila il modulo per ricevere informazioni sulle offerte disponibili. Ti ricontatteremo senza impegno.
        </p>
        <p className="mb-8 text-xs text-light-muted/70">
          Il servizio è completamente gratuito. Nessun obbligo di sottoscrizione.
        </p>

        {formStatus === "success" && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-green-50 text-green-700 rounded-xl flex items-center border border-green-200 text-sm"
          >
            <FontAwesomeIcon icon={faCheckCircle} className="mr-3" />
            Grazie per averci contattato! Ti ricontatteremo per una consulenza gratuita e senza impegno.
          </motion.div>
        )}

        {formStatus === "error" && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-red-50 text-red-700 rounded-xl flex items-center border border-red-200 text-sm"
          >
            <FontAwesomeIcon icon={faExclamationCircle} className="mr-3" />
            Si è verificato un errore nell'invio. Riprova più tardi o contattaci telefonicamente.
          </motion.div>
        )}

        <form ref={formRef} onSubmit={handleSubmit} className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label htmlFor="nome" className="block mb-1.5 text-light-text font-medium text-sm">Nome *</label>
              <input
                type="text" id="nome" name="nome"
                value={formData.nome} onChange={handleChange}
                className={inputClasses(!!errors.nome)}
                placeholder="Il tuo nome" required
              />
              {errors.nome && <p className="mt-1 text-red-500 text-xs">{errors.nome}</p>}
            </div>
            <div>
              <label htmlFor="cognome" className="block mb-1.5 text-light-text font-medium text-sm">Cognome *</label>
              <input
                type="text" id="cognome" name="cognome"
                value={formData.cognome} onChange={handleChange}
                className={inputClasses(!!errors.cognome)}
                placeholder="Il tuo cognome" required
              />
              {errors.cognome && <p className="mt-1 text-red-500 text-xs">{errors.cognome}</p>}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label htmlFor="email" className="block mb-1.5 text-light-text font-medium text-sm">Email *</label>
              <input
                type="email" id="email" name="email"
                value={formData.email} onChange={handleChange}
                className={inputClasses(!!errors.email)}
                placeholder="La tua email" required
              />
              {errors.email && <p className="mt-1 text-red-500 text-xs">{errors.email}</p>}
            </div>
            <div>
              <label htmlFor="telefono" className="block mb-1.5 text-light-text font-medium text-sm">Telefono</label>
              <input
                type="tel" id="telefono" name="telefono"
                value={formData.telefono} onChange={handleChange}
                className={inputClasses(false)}
                placeholder="Il tuo numero di telefono"
              />
            </div>
          </div>

          <div>
            <label htmlFor="servizio" className="block mb-1.5 text-light-text font-medium text-sm">Servizio di interesse</label>
            <select
              id="servizio" name="servizio"
              value={formData.servizio} onChange={handleChange}
              className={inputClasses(false)}
            >
              <option value="">Seleziona un servizio</option>
              <option value="luce">Offerte Luce</option>
              <option value="gas">Offerte Gas</option>
              <option value="internet">Offerte Internet</option>
              <option value="business">Soluzioni Business</option>
              <option value="altro">Altro</option>
            </select>
          </div>

          <div>
            <label htmlFor="messaggio" className="block mb-1.5 text-light-text font-medium text-sm">Messaggio *</label>
            <textarea
              id="messaggio" name="messaggio"
              value={formData.messaggio} onChange={handleChange}
              rows={5}
              className={inputClasses(!!errors.messaggio)}
              placeholder="Il tuo messaggio" required
            ></textarea>
            {errors.messaggio && <p className="mt-1 text-red-500 text-xs">{errors.messaggio}</p>}
          </div>

          <div className="flex items-start">
            <input
              type="checkbox" id="privacy"
              checked={privacyConsent}
              onChange={(e) => setPrivacyConsent(e.target.checked)}
              className="mt-0.5 mr-2 accent-primary"
              required
            />
            <label htmlFor="privacy" className="text-light-muted text-xs leading-relaxed">
              Acconsento al trattamento dei miei dati personali in conformità con la
              <a href="/privacy-policy" className="text-primary hover:text-secondary ml-1 font-medium">Privacy Policy</a>. 
              Comprendo che il servizio è gratuito e che la sottoscrizione di eventuali contratti è facoltativa e a mia discrezione.
            </label>
          </div>

          <motion.button
            type="submit"
            disabled={formStatus === "loading"}
            className={`btn ${formStatus === "loading" ? "bg-gray-300 cursor-not-allowed" : "btn-primary"} w-full md:w-auto flex items-center justify-center`}
            whileHover={formStatus !== "loading" ? { scale: 1.02 } : {}}
            whileTap={formStatus !== "loading" ? { scale: 0.98 } : {}}
          >
            {formStatus === "loading" ? (
              <>
                <FontAwesomeIcon icon={faSpinner} className="mr-2 animate-spin" />
                Invio in corso...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faPaperPlane} className="mr-2" />
                Invia Messaggio
              </>
            )}
          </motion.button>
        </form>
      </div>
    </motion.div>
  );
};

export default ContactForm;
